import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    EwcCodeCreatePayload,
    EwcCodeUpdatePayload,
    IEwcCodeDetail,
} from "@/customTypings/reference/EwcCode";
import { EWC_CREATE_CONFLICT } from "@/customTypings/reference/EwcCode";
import type { ReferenceRestoreDeletedPayload } from "@/utils/reference/referenceCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";
import { postReferenceCreate } from "@/utils/reference/referenceCreateConflict";

export const ewcCodesService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/referencedata/ewcs/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IEwcCodeDetail>;
}

function create(payload: EwcCodeCreatePayload) {
    return postReferenceCreate<IEwcCodeDetail>(
        `/api/referencedata/ewcs/create`,
        payload,
        EWC_CREATE_CONFLICT.deletedRestoreAvailable
    );
}

function restoreDeletedWithCreateValues(payload: ReferenceRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/ewcs/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IEwcCodeDetail>;
}

function update(payload: EwcCodeUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/ewcs/update`, requestOptions).then(handleResponse) as Promise<
        UpdateMutationResult<IEwcCodeDetail>
    >;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/ewcs/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
